import {
  AllocationSignal,
  DateInfo,
  FrontendRegistration,
  RegistrationActivity,
  TimeZoneNamed,
} from "@timeedit/registration-schemas";
import {
  Mapping,
  MappingShorthandField,
  UnsafeRecord,
  isDefined,
  isPositiveNumber,
  parseDate,
  replaceBraces,
  stringHasContent,
} from "@timeedit/registration-shared";
import { Segmented, Typography } from "@timeedit/ui-components";
import { Empty, List } from "@timeedit/ui-components/antd";
import cn from "classnames";
import { uniq } from "lodash-es";
import {
  RefObject,
  createRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ConflictNotification } from "../../Conflict";
import { WaitlistNotification } from "../../WaitlistNotification";
import {
  IdContext,
  RegistrationContext,
  UtilContext,
} from "../RegistrationContexts";
import { RegistrationOptionsContext } from "../RegistrationOptions";
import { RegistrationRequiredTranslations } from "../types";
import { makeActivityRefs } from "./courseList.helpers";
import "./courseList.scss";
import RightOutlined from "@timeedit/ui-components/icons/RightOutlined";

const { Item } = List;

type SortType = "Course" | "Class type";
// TODO: Do we want to remove the sort by, or keep it? Consider enabling it if so.
// If we ever enable this again it needs to be throughly tested because we have
// added new logic since it was last used, for instance not showing empty activities for students
const showSortBy = false;

export function CourseList({
  focusRef,
}: {
  focusRef?: React.RefObject<HTMLInputElement>;
}): React.JSX.Element {
  const { registration } = useContext(RegistrationContext);
  const { translations, mapping, navigate } = useContext(UtilContext);
  const { id } = useContext(IdContext);
  const [sortType, setSortType] = useState<SortType>("Course");
  const itemRefs = makeActivityRefs(registration);
  const { isDesktop, headingLevel } = useContext(RegistrationOptionsContext);

  useEffect(() => {
    if (!isDefined(id)) return;
    const ref = itemRefs[id];
    if (!isDefined(ref)) return;

    // Just scroll once we start the application
    ref.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortBy = showSortBy && (
    <>
      {translations.sortBy}:{" "}
      <Segmented<SortType>
        options={[
          {
            value: "Course",
            label: "Course",
            icon: <div>{translations.COURSE}</div>, // TODO Replace with useNames() typename
          },
          {
            value: "Class type",
            label: "Class type",
            icon: <div>{translations.classType}</div>, // TODO Replace with useNames() fieldname
          },
        ]}
        onChange={(value) => setSortType(value)}
      />
    </>
  );

  return (
    <>
      <Typography.Title
        id="course-list-header"
        level={2}
        semanticLevel={headingLevel(2)}
        className="course-list__list-header"
      >
        {replaceBraces(translations.typeList, mapping.typename("course"))}
      </Typography.Title>
      {sortBy}
      <div
        className={cn("course-list__container", {
          "course-list__container--desktop": isDesktop,
        })}
      >
        {Object.keys(registration.courses).length === 0 ? (
          <Empty
            className="course-list__empty-message"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={translations.noEnrolledCourses}
          />
        ) : (
          <CourseOrClassList
            sortType={sortType}
            mapping={mapping}
            registration={registration}
          />
        )}
      </div>
    </>
  );

  type CourseOrClassList = {
    sortType: SortType;
    mapping: Mapping;
    registration: FrontendRegistration;
    isDesktop?: boolean;
  };
  function CourseOrClassList({
    sortType,
  }: CourseOrClassList): React.JSX.Element {
    return useMemo(
      () =>
        sortType === "Course" ? (
          <>{courseList({ mapping, registration })}</>
        ) : (
          <>{classTypeList({ mapping, registration })}</>
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sortType, mapping, registration]
    );
  }

  type CourseListProps = {
    registration: FrontendRegistration;
    mapping: Mapping;
  };
  function courseList({
    mapping,
    registration,
  }: CourseListProps): React.ReactNode[] {
    return registration.courseIds
      .map((courseId) => {
        const course = registration.courses[courseId];
        if (!isDefined(course)) {
          return null;
        }

        const label = mapping.parse("courseLabel", course?.teObject);
        const title = mapping.parse("courseTitle", course?.teObject);

        const semester = mapping.parse("semester", course?.teObject);

        const activities = course.children
          .map((id) => registration.activities[id])
          .filter(isDefined);

        const closeDate = uniq(
          activities
            .map((activity) =>
              getSharedDate({
                mapping,
                registration,
                activity,
                shortHand: "endDate",
              })
            )
            .filter(stringHasContent)
        );

        const startDate = uniq(
          activities
            .map((activity) =>
              getSharedDate({
                mapping,
                registration,
                activity,
                shortHand: "startDate",
              })
            )
            .filter(stringHasContent)
        );

        const oneCloseDate = closeDate.length === 1;

        const closeDateText =
          oneCloseDate && isDefined(closeDate[0])
            ? presentEndDate(
                translations,
                closeDate[0],
                startDate[0],
                registration.timezone,
                registration.serverDateTime
              )
            : "";

        return (
          <List
            key={`list-${title}-${label}-${semester}`}
            className="course-list"
            header={
              <CourseListHeader
                courseId={course.id}
                label={label}
                title={title}
                semester={semester}
                closeDateText={closeDateText}
              />
            }
            bordered
            dataSource={activities}
            renderItem={(activity) => (
              <CourseListItem
                itemRef={itemRefs[activity.id]}
                onClick={() => handleGroupClick(activity.id)}
                hasConflict={activity.hasConflicts}
                isOnWaitlist={activity.hasWaitlist}
                title={activity.label}
                open={activity.open}
                signal={activity.allocationSignal}
                activityId={activity.id}
                courseListTranslations={translations}
              />
            )}
          />
        );
      })
      .filter(isDefined);
  }
  function handleGroupClick(id: number): void {
    navigate(id.toString());
    if (focusRef?.current) {
      focusRef.current.focus();
    }
  }

  type ActivityWithCourseInfo = {
    activity: RegistrationActivity;
    courseLabel: string;
    title: string;
    semester: string;
    closeDate: string;
  };
  type ClassTypeListProps = {
    registration: FrontendRegistration;
    mapping: Mapping;
  };
  function classTypeList({
    mapping,
    registration,
  }: ClassTypeListProps): React.JSX.Element[] {
    const activitiesWithCourseInfo = Object.values(registration.activities)
      .filter(isDefined)
      .reduce<UnsafeRecord<string, ActivityWithCourseInfo[]>>(
        (obj, activity) => {
          const activityObjToPush = activity;

          if (!isDefined(obj[activity.label])) obj[activity.label] = [];

          const courseLabel = mapping.parse(
            "courseLabel",
            registration.courses[activity.parentId]?.teObject
          );
          const title = mapping.parse(
            "courseTitle",
            registration.courses[activity.parentId]?.teObject
          );

          const semester = mapping.parse(
            "semester",
            registration.courses[activity.parentId]?.teObject
          );

          const closeDate = mapping.parse(
            "endDate",
            registration.courses[activity.parentId]?.teObject
          );

          obj[activity.label]?.push({
            activity: activityObjToPush,
            courseLabel,
            title,
            semester,
            closeDate,
          });

          return obj;
        },
        {}
      );

    return Object.entries(activitiesWithCourseInfo).map(
      ([key, activitiesWithCourseInfo]) => {
        return (
          <List
            key={`list-${key}`}
            className="course-list"
            header={
              <CourseListHeader
                label={""}
                title={key}
                semester={""}
                closeDateText={""}
              />
            }
            bordered
            dataSource={activitiesWithCourseInfo}
            renderItem={(activityWithCourseInfo) => (
              <CourseListItem
                itemRef={itemRefs[activityWithCourseInfo.activity.id]}
                onClick={() =>
                  handleGroupClick(activityWithCourseInfo.activity.id)
                }
                hasConflict={activityWithCourseInfo.activity.hasConflicts}
                isOnWaitlist={activityWithCourseInfo.activity.hasWaitlist}
                title={
                  <div>
                    <Typography.Text strong>
                      {activityWithCourseInfo.title}
                    </Typography.Text>
                    <br />
                    {activityWithCourseInfo.courseLabel}{" "}
                    {activityWithCourseInfo.semester}
                  </div>
                }
                signal={activityWithCourseInfo.activity.allocationSignal}
                activityId={activityWithCourseInfo.activity.id}
                open={activityWithCourseInfo.activity.open}
                courseListTranslations={translations}
              />
            )}
          />
        );
      }
    );
  }
}

type CourseListHeaderProps = {
  label: string;
  courseId?: number;
  title: string;
  semester: string;
  closeDateText: string;
};

export function CourseListHeader(
  props: CourseListHeaderProps
): React.JSX.Element {
  const { headingLevel } = useContext(RegistrationOptionsContext);
  return (
    <div data-course-id={props.courseId}>
      <div className="course-list__header-container">
        <Typography.Title
          level={5}
          semanticLevel={headingLevel(3)}
          className="course-list__header-name"
        >
          {props.title}
        </Typography.Title>
        {props.label}
      </div>
      <div className="course-list__header-container">
        <Typography.Text>{props.closeDateText}</Typography.Text>
        {props.semester}
      </div>
    </div>
  );
}

interface CourseListItem {
  activityId: number;
  title: React.JSX.Element | string;
  open?: boolean;
  signal: AllocationSignal;
  hasConflict: boolean;
  isOnWaitlist: boolean;
  onClick: (e: React.MouseEvent | React.KeyboardEvent) => void;
  courseListTranslations: {
    registered: string;
    notRegistered: string;
  };
  itemRef?: RefObject<HTMLDivElement>;
}
export function CourseListItem({
  activityId,
  title,
  signal,
  hasConflict,
  isOnWaitlist,
  onClick,
  courseListTranslations,
  itemRef = createRef<HTMLDivElement>(),
}: CourseListItem): React.JSX.Element {
  const { id } = useContext(IdContext);
  const { registration, mode } = useContext(RegistrationContext);
  const { slots } = useContext(RegistrationOptionsContext);

  useEffect(() => {
    // To see which item is selected
    if (id === activityId) {
      itemRef?.current?.classList.add("course-list__item--selected");
    } else {
      itemRef?.current?.classList.remove("course-list__item--selected");
    }
  }, [id, itemRef, activityId]);

  return (
    <Item
      data-activity-id={activityId}
      role="button"
      ref={itemRef}
      className="course-list__item"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onClick(e);
        }
      }}
      onClick={(e) => {
        onClick(e);
      }}
    >
      <div className="course-list__item-left">
        {typeof title === "string" ? (
          <Typography.Text strong>{title}</Typography.Text>
        ) : (
          title
        )}
      </div>
      <div className="course-list__flex-row">
        <div className="course-list__item-right">
          {slots.activityBadge({
            signal,
            translations: courseListTranslations,
            registration,
            activityId,
          })}
          {slots.activityDescription({ mode, registration, activityId })}
          <div className="course-list__notifications-container">
            {hasConflict && <ConflictNotification useText />}
            {isOnWaitlist && <WaitlistNotification useText />}
          </div>
        </div>
        <RightOutlined alt="Navigate" className="course-list__item-icon" />
      </div>
    </Item>
  );
}

type CloseDateProps = {
  activity?: RegistrationActivity;
  registration: FrontendRegistration;
  mapping: Mapping;
  shortHand: Extract<MappingShorthandField, "startDate" | "endDate">;
};

/**
 * @returns The date if all the groups has the same date.
 */
export function getSharedDate({
  mapping,
  registration,
  activity,
  shortHand,
}: CloseDateProps): string {
  let result = undefined;
  if (!isDefined(activity)) return "";
  for (const id of activity.children) {
    const group = registration.groups[id];
    if (!isDefined(group)) continue;
    const date = mapping.string(shortHand, group.teObject);
    if (!isDefined(result)) {
      result = date;
      continue;
    }
    if (result !== date) {
      return "";
    }
  }
  return result || "";
}

function presentEndDate(
  translations: RegistrationRequiredTranslations,
  closeDate: string,
  openDate: string | undefined,
  timezone: TimeZoneNamed,
  serverDateTime?: DateInfo
): string {
  const serverTimezone = serverDateTime?.timezone ?? "";
  const serverBegin = parseDate(openDate, serverTimezone);
  if (!serverBegin.success) {
    return `${closeDate}`;
  }
  const serverEnd = parseDate(closeDate, serverTimezone);
  if (!serverEnd.success) {
    return `${closeDate}`;
  }
  const userBegin = parseDate(serverBegin.data.unix, timezone.user.identifier);
  if (!userBegin.success) {
    return `${closeDate}`;
  }
  const userEnd = parseDate(serverEnd.data.unix, timezone.user.identifier);
  if (!userEnd.success) {
    return `${closeDate}`;
  }
  const start = userBegin.data.local;
  const end = userEnd.data.local;
  const range = new IntRange(serverBegin.data.unix, serverEnd.data.unix);
  const now = serverDateTime?.unix ?? 0;
  if (!isDefined(now) || !isDefined(openDate) || !range.isValid()) {
    return `${translations.registrationOpenUntil} ${end}`;
  }
  if (range.isBefore(now)) {
    return `${translations.registrationOpens} ${start}`;
  }
  if (range.includes(now)) {
    return `${translations.registrationOpenUntil} ${end}`;
  }
  if (range.isAfter(now)) {
    return `${translations.registrationClosed} ${end}`;
  }
  return `${closeDate}`;
}

class IntRange {
  start: number;
  end: number;
  constructor(start: number, end: number) {
    this.start = start;
    this.end = end;
  }

  isValid(): boolean {
    return (
      isPositiveNumber(this.start) &&
      isPositiveNumber(this.end) &&
      this.start <= this.end
    );
  }

  includes(value: number): boolean {
    return !this.isBefore(value) && !this.isAfter(value);
  }

  isBefore(value: number): boolean {
    return value < this.start;
  }

  isAfter(value: number): boolean {
    return value > this.end;
  }
}
